import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash, MapPinArea } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// All 8 supported assignment roles. Origin / destination / waypoint also feed
// `evaluate_geofence_events()` for arrival / departure / route-deviation events.
export const GEOFENCE_ROLES = [
  "origin", "destination", "waypoint",
  "transfer", "inspection", "storage", "exception", "custom",
];
const ROLE_COLOR = {
  origin:      "bg-amber-500/10 text-amber-300 border-amber-500/30",
  destination: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  waypoint:    "bg-sky-500/10 text-sky-300 border-sky-500/30",
  transfer:    "bg-violet-500/10 text-violet-300 border-violet-500/30",
  inspection:  "bg-fuchsia-500/10 text-fuchsia-300 border-fuchsia-500/30",
  storage:     "bg-cyan-500/10 text-cyan-300 border-cyan-500/30",
  exception:   "bg-red-500/10 text-red-300 border-red-500/30",
  custom:      "bg-zinc-700/30 text-zinc-300 border-zinc-700",
};

/**
 * Reusable geofence-assignment editor.
 *
 * Two modes:
 *   - In ShipmentForm (no shipmentId yet): `value` is a local array of pending
 *     assignments; `onChange` returns the updated array. The parent flushes
 *     them via POST `/api/geofences/assign-to-shipment/{id}` after create.
 *   - In ShipmentDetail (shipmentId provided): live mode — additions and
 *     removals call the backend immediately and reload from the response.
 */
export default function GeofenceAssignmentsEditor({
  shipmentId = null,
  value: pendingValue,
  onChange,
  initialAssignments = [],
  compact = false,
  onAfterChange,
}) {
  const live = !!shipmentId;
  const [geofences, setGeofences] = useState([]);
  const [assignments, setAssignments] = useState(initialAssignments || []);
  const [draftGid, setDraftGid] = useState("");
  const [draftRole, setDraftRole] = useState("origin");
  const [draftLabel, setDraftLabel] = useState("");

  // In pending-mode the source of truth is the parent
  useEffect(() => {
    if (!live && Array.isArray(pendingValue)) setAssignments(pendingValue);
  }, [pendingValue, live]);

  useEffect(() => {
    axios.get(`${API}/geofences`).then((r) => setGeofences(r.data.items || [])).catch(() => {});
  }, []);

  const gById = (id) => geofences.find((g) => g.id === id);

  const add = async () => {
    if (!draftGid) return;
    const existing = assignments.find((a) => a.geofence_id === draftGid && a.role === draftRole);
    if (existing) return;  // idempotent
    const newAssignment = {
      geofence_id: draftGid,
      role: draftRole,
      label: draftLabel || null,
    };
    if (live) {
      await axios.post(`${API}/geofences/assign-to-shipment/${shipmentId}`, newAssignment);
      const next = [...assignments, newAssignment];
      setAssignments(next);
      onAfterChange?.(next);
    } else {
      const next = [...assignments, newAssignment];
      setAssignments(next);
      onChange?.(next);
    }
    setDraftGid(""); setDraftLabel("");
  };

  const remove = async (a) => {
    if (live) {
      await axios.post(`${API}/geofences/detach-from-shipment/${shipmentId}/${a.geofence_id}?role=${a.role}`);
      const next = assignments.filter((x) => !(x.geofence_id === a.geofence_id && x.role === a.role));
      setAssignments(next);
      onAfterChange?.(next);
    } else {
      const next = assignments.filter((x) => !(x.geofence_id === a.geofence_id && x.role === a.role));
      setAssignments(next);
      onChange?.(next);
    }
  };

  return (
    <div data-testid="geofence-assignments-editor">
      {!compact && (
        <Label className="text-[10px] uppercase tracking-wider text-zinc-400 flex items-center gap-1 mb-2">
          <MapPinArea size={11} weight="duotone" /> Geofence Assignments
          <span className="text-zinc-600 normal-case font-normal tracking-normal ml-1">
            ({assignments.length})
          </span>
        </Label>
      )}

      {/* Add row */}
      <div className="grid grid-cols-12 gap-2 mb-2">
        <select value={draftGid} onChange={(e) => setDraftGid(e.target.value)}
          className="col-span-5 bg-zinc-900 border border-zinc-800 text-zinc-200 text-xs rounded-sm px-2 h-8"
          data-testid="gfa-geofence">
          <option value="">— Select geofence —</option>
          {geofences.map((g) => (
            <option key={g.id} value={g.id}>{g.name} ({g.type})</option>
          ))}
        </select>
        <select value={draftRole} onChange={(e) => setDraftRole(e.target.value)}
          className="col-span-3 bg-zinc-900 border border-zinc-800 text-zinc-200 text-xs rounded-sm px-2 h-8 capitalize"
          data-testid="gfa-role">
          {GEOFENCE_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <Input value={draftLabel} onChange={(e) => setDraftLabel(e.target.value)}
          placeholder="Label (optional)"
          className="col-span-3 bg-zinc-900 border-zinc-800 text-xs h-8"
          data-testid="gfa-label" />
        <Button type="button" onClick={add} disabled={!draftGid}
          className="col-span-1 bg-teal-500 hover:bg-teal-400 text-zinc-950 text-xs h-8 px-2"
          data-testid="gfa-add">
          <Plus size={12} />
        </Button>
      </div>

      {/* Current assignments */}
      <div className="space-y-1">
        {assignments.length === 0 ? (
          <p className="text-[10px] text-zinc-600 italic">No geofences assigned. Add an origin, destination, or any other operational stop.</p>
        ) : assignments.map((a, i) => {
          const g = gById(a.geofence_id);
          return (
            <div key={`${a.geofence_id}-${a.role}-${i}`}
              className="flex items-center gap-2 px-2 py-1.5 bg-zinc-900/60 border border-zinc-800 rounded-sm text-xs"
              data-testid={`gfa-row-${a.role}`}>
              <span className={`text-[10px] uppercase font-medium tracking-wider px-1.5 py-0.5 rounded-sm border ${ROLE_COLOR[a.role] || ROLE_COLOR.custom}`}>
                {a.role}
              </span>
              <span className="text-zinc-200 truncate flex-1">{g?.name || a.geofence_id}</span>
              {g?.type && <span className="text-[10px] text-zinc-500 font-mono">{g.type}</span>}
              {a.label && <span className="text-[10px] text-teal-300 italic">"{a.label}"</span>}
              <button type="button" onClick={() => remove(a)}
                className="text-zinc-500 hover:text-red-300 p-1"
                data-testid={`gfa-remove-${a.role}-${a.geofence_id}`}>
                <Trash size={11} />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
